import { FC } from "react";
import { v4 as uuidv4 } from "uuid";
import { ArrayField, UseFormMethods } from "react-hook-form";
import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import {
	availableMmsTypeForMonthlyStoragePeriods,
	availableMmsTypeForOverOrUnderStoragePeriod,
} from "../../../../models/Baseline/DairyV2Baseline";
import {
	CattleManureManagementSystemType,
	ManurePhase,
	StoragePeriod,
} from "../../../../models/Baseline/CattleBaseline";
import RowControlledTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { FormType } from "../../common";
import { CattleHousingAndManure } from "../../../../models/Intervention/CattleIntervention";
import RowSelectWithMetricsV2 from "../../../../../modules/Farms/Intervention/RowSelectWithMetricsV2";
import { Spacer } from "../../../helpers/Spacer";
import SustellMMSAdditionalItem from "../../../../../modules/Helpers/SustellMMSAdditionalItem";
import { enumToOptionsArrayWithTranslatedStrings } from "../../../../utils/obj-utils";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import { DairyV2Intervention } from "../../../../models/Intervention/DairyV2Intervention";

type DairyV2InterventionMMSComponentProps = {
	formContext: UseFormMethods<DairyV2Intervention>;
	stageIndex?: number;
	isViewMode?: boolean;
	openDescriptionDialog: () => void;
	isDairyCowStage: boolean;
	fieldItemPrefix: string;
	baseline: CattleHousingAndManure;
	formType: FormType;
	mmsCollection: {
		mmsAdditions: Partial<ArrayField<Record<string, any>, "keyId">>[];
		appendMMSAdditions: (value: Partial<Record<string, any>> | Partial<Record<string, any>>[], shouldFocus?: boolean) => void;
		removeMMSAdditions: (index?: number | number[]) => void;
	}
};

type MMSInput = {
	mmsType: string;
	storagePeriod: string;
	share: string;
	id: string;
};

const DairyV2InterventionMMSComponent: FC<DairyV2InterventionMMSComponentProps> = ({
	formContext,
	formType = FormType.Add,
	stageIndex = 0,
	isViewMode = false,
	openDescriptionDialog,
	isDairyCowStage,
	fieldItemPrefix,
	baseline,
	mmsCollection,
}) => {
	const intl = useIntl();
	const classes = processAndStageStylesV2() as CSSClassesList;
	const baseItemPrefix = `${fieldItemPrefix}.manureSystems`;
	const { mmsAdditions, appendMMSAdditions, removeMMSAdditions } = mmsCollection;

	const allMmsOptions = enumToOptionsArrayWithTranslatedStrings(
		CattleManureManagementSystemType,
		intl,
		"SUSTELL.DAIRY.ENUMS.MMS_TYPE"
	);

	const allStorageOptions = enumToOptionsArrayWithTranslatedStrings(
		StoragePeriod,
		intl,
		"SUSTELL.DAIRY.ENUMS.MMS_STORAGE"
	);

	const getAllSelectedMms = (): Array<MMSInput> => {
		const manureSystems: Array<MMSInput> = formContext.getValues(`${fieldItemPrefix}.manureSystems`) || [];
		const manureSystemsAdditions: Array<MMSInput> = formContext.getValues(`${fieldItemPrefix}.manureSystemsAdditions`) || [];

		return [...manureSystems, ...manureSystemsAdditions];
	};

	const isSingleMms = () => getAllSelectedMms().length === 1;
	const isMmsEmpty = () => getAllSelectedMms().length === 0;

	const addMMS = () => {
		const emptyMMS: MMSInput = {
			id: uuidv4(),
			mmsType: "",
			storagePeriod: "",
			share: isMmsEmpty() ? "100" : "",
		};

		if (isSingleMms()) {
			formContext.setValue(`${fieldItemPrefix}.manureSystemsAdditions.${0}.share`, "");
		}

		appendMMSAdditions(emptyMMS);
	};

	const isStorageFieldRequired = (
		controlMmsType: CattleManureManagementSystemType,
		baselineMmsType: string
	): boolean => {
		const allAvailableMmsTypes = new Set([
			...availableMmsTypeForMonthlyStoragePeriods,
			...availableMmsTypeForOverOrUnderStoragePeriod,
		]);

		if (isFieldFilled(controlMmsType))
			return allAvailableMmsTypes.has(controlMmsType);

		return allAvailableMmsTypes.has(baselineMmsType as CattleManureManagementSystemType);
	};

	const mmsSelectHandler = (type: CattleManureManagementSystemType, index: number) => {
		if (type === CattleManureManagementSystemType.DailySpread) {
			formContext.setValue(`${baseItemPrefix}.${index}.manurePhase`, ManurePhase.LiquidSlurry);
		} else {
			formContext.setValue(`${baseItemPrefix}.${index}.manurePhase`, undefined);
		}

		if (!isStorageFieldRequired(type, ""))
			formContext.setValue(`${baseItemPrefix}.${index}.storagePeriod`, "")
	};

	const getCorrectPeriodOptions = (index: number, baselineMmsType: string) => {
		let mmsType: CattleManureManagementSystemType = formContext.getValues(`${baseItemPrefix}.${index}.mmsType`);
		if (!isFieldFilled(mmsType))
			mmsType = baselineMmsType as CattleManureManagementSystemType;

		const underOverPeriod: string[] = [
			StoragePeriod.UNDER_ONE_MONTH,
			StoragePeriod.OVER_ONE_MONTH
		];

		const monthlyPeriod: string[] = [
			StoragePeriod.ONE_MONTH,
			StoragePeriod.THREE_MONTHS,
			StoragePeriod.FOUR_MONTHS,
			StoragePeriod.SIX_MONTHS,
			StoragePeriod.TWELVE_MONTHS,
		]

		if (availableMmsTypeForOverOrUnderStoragePeriod.includes(mmsType))
			return allStorageOptions.filter((opt) => underOverPeriod.includes(opt.value));

		return allStorageOptions.filter((opt) => monthlyPeriod.includes(opt.value));
	};

	const timeInHousing = formContext.getValues(`${fieldItemPrefix}.timeInHousing`);
	const shouldDisplayMMSRows: boolean =
		timeInHousing === undefined
		|| timeInHousing === ""
		|| Number(timeInHousing) > 0;

	const showOriginalValue = (controlValue: string, baselineValue: string) => {
		if (!isFieldFilled(controlValue)) {
			return false;
		}

		return controlValue !== baselineValue;
	};

	return (
		<div>
			{isDairyCowStage && (
				<DsmGrid className={isViewMode ? classes.dsmGridTwoColumn : classes.dsmGridTwoColumnsInterventionComponent}
				>
					{(isViewMode || !shouldDisplayMMSRows) &&
						!isFieldFilled(formContext.getValues(`${fieldItemPrefix}.timeInHousingTied`)) ? (
						<ReactHookDsmInput
							type="number"
							name={`${fieldItemPrefix}.originalTimeInHousingTied`}
							label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.HOUSING.TIME_SPENT_IN_HOUSING_TIED' })}
							tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.HOUSING.TIME_SPENT_IN_HOUSING_TIED.TOOLTIP' })}
							adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.HOUSING.TIME_SPENT_IN_HOUSING_TIED.ADORNMENT' })}
							defaultValue={baseline.timeInHousingTied as string}
							disabled
							required
						/>
					) : (
						<RowControlledTextFieldWithMetrics
							name={`${fieldItemPrefix}.timeInHousingTied`}
							label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.HOUSING.TIME_SPENT_IN_HOUSING_TIED' })}
							tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.HOUSING.TIME_SPENT_IN_HOUSING_TIED.TOOLTIP' })}
							metricUnit={' '}
							baseline={baseline.timeInHousingTied}
							disabled={!shouldDisplayMMSRows || formType === "view" || isViewMode}
							minAllowedValue={0}
							maxAllowedValue={100}
							isDefaultMetricSet
							displayTypeSelect={!isViewMode}
						/>
					)}
				</DsmGrid>
			)}
			<p className={classes.headingTitleVariant} style={{ paddingTop: 0 }}>
				{intl.formatMessage({
					id: "SUSTELL.STAGE.DAIRY.MMS.TIME_SPENT_IN_HOUSING.DESCRIPTION",
				})}
			</p>

			{shouldDisplayMMSRows && baseline?.manureSystems && baseline?.manureSystems.map((item, index) => (
				<DsmGrid className={classes.dsmGridThreeColumnFirstBigOthersSmall}>
					{isViewMode ? (
						<>
							{showOriginalValue(
								formContext.getValues(`${baseItemPrefix}.${index}.mmsType`) as string,
								item.mmsType as string) ? (
								<RowSelectWithMetricsV2
									name={`${baseItemPrefix}.${index}.mmsType`}
									label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_TYPE' })}
									tooltip={intl.formatMessage({
										id: isDairyCowStage
											? 'SUSTELL.STAGE.DAIRY.MMS.MMS_TYPE.TOOLTIP'
											: 'SUSTELL.STAGE.DAIRY.MMS.CONTINUOUS_GROWING.MMS_TYPE.TOOLTIP',
									})}
									options={allMmsOptions}
									baseline={item.mmsType}
									disabled
								/>
							) : (
								<ReactHookDsmSelect
									name={`${baseItemPrefix}.${index}.originalMmsType`}
									label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_TYPE' })}
									tooltip={intl.formatMessage({
										id: isDairyCowStage
											? 'SUSTELL.STAGE.DAIRY.MMS.MMS_TYPE.TOOLTIP'
											: 'SUSTELL.STAGE.DAIRY.MMS.CONTINUOUS_GROWING.MMS_TYPE.TOOLTIP',
									})}
									options={allMmsOptions}
									defaultValue={item.mmsType}
									disabled
								/>
							)}
							{isStorageFieldRequired(formContext.getValues(`${baseItemPrefix}.${index}.mmsType`), item.mmsType ?? "") ?
								(showOriginalValue(
									formContext.getValues(`${baseItemPrefix}.${index}.storagePeriod`) as string,
									item.storagePeriod as string) ? (
									<RowSelectWithMetricsV2
										name={`${baseItemPrefix}.${index}.storagePeriod`}
										label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_STORAGE_PERIOD' })}
										tooltip={intl.formatMessage({
											id: isDairyCowStage
												? 'SUSTELL.STAGE.DAIRY.MMS.MMS_TYPE.TOOLTIP'
												: 'SUSTELL.STAGE.DAIRY.MMS.CONTINUOUS_GROWING.MMS_TYPE.TOOLTIP',
										})}
										options={allStorageOptions}
										baseline={item.storagePeriod}
										disabled
										originalLabel={item.storagePeriod !== undefined}
									/>
								) : (
									<ReactHookDsmSelect
										name={`${baseItemPrefix}.${index}.originalStoragePeriod`}
										label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_STORAGE_PERIOD' })}
										tooltip={intl.formatMessage({
											id: isDairyCowStage
												? 'SUSTELL.STAGE.DAIRY.MMS.MMS_TYPE.TOOLTIP'
												: 'SUSTELL.STAGE.DAIRY.MMS.CONTINUOUS_GROWING.MMS_TYPE.TOOLTIP',
										})}
										options={allStorageOptions}
										defaultValue={item.storagePeriod}
										disabled
									/>
								)) : (<div />)}
							{!isSingleMms() &&
								<ReactHookDsmInput
									name={`${baseItemPrefix}.${index}.originalShare`}
									label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_SHARE' })}
									tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_SHARE.TOOLTIP' })}
									adornment="%"
									defaultValue={item.share ?? ''}
									type="number"
									disabled
								/>}
						</>
					) : (
						<>
							<div>
								<RowSelectWithMetricsV2
									name={`${baseItemPrefix}.${index}.mmsType`}
									label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_TYPE' })}
									tooltip={intl.formatMessage({
										id: isDairyCowStage
											? 'SUSTELL.STAGE.DAIRY.MMS.MMS_TYPE.TOOLTIP'
											: 'SUSTELL.STAGE.DAIRY.MMS.CONTINUOUS_GROWING.MMS_TYPE.TOOLTIP',
									})}
									options={allMmsOptions}
									baseline={item.mmsType}
									disabled={false}
									changeHandler={(e: React.ChangeEvent<HTMLSelectElement>) => {
										mmsSelectHandler(e.target.value as CattleManureManagementSystemType, index)
									}}
								/>
								<Spacer />
							</div>
							<div>
								{isStorageFieldRequired(formContext.getValues(`${baseItemPrefix}.${index}.mmsType`), item.mmsType ?? "") &&
									<>
										<RowSelectWithMetricsV2
											name={`${baseItemPrefix}.${index}.storagePeriod`}
											label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_STORAGE_PERIOD' })}
											tooltip={intl.formatMessage({
												id: isDairyCowStage
													? 'SUSTELL.STAGE.DAIRY.MMS.MMS_TYPE.TOOLTIP'
													: 'SUSTELL.STAGE.DAIRY.MMS.CONTINUOUS_GROWING.MMS_TYPE.TOOLTIP',
											})}
											options={getCorrectPeriodOptions(index, item.mmsType ?? "")}
											baseline={item.storagePeriod}
											disabled={false}
											originalLabel={item.storagePeriod !== undefined}
										/>
										<Spacer />
									</>}
							</div>
							<div>
								<ReactHookDsmInput
									name={`${baseItemPrefix}.${index}.share`}
									label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_SHARE' })}
									tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_SHARE.TOOLTIP' })}
									adornment="%"
									defaultValue={item.share ?? ''}
									type="number"
									disabled={false}
								/>
								<Spacer />
							</div>
						</>
					)}
				</DsmGrid>
			))}

			{mmsAdditions?.length > 0 && mmsAdditions?.map((item, index) => (
				<div key={item.keyId}>
					<div
						className={classes.dsmGridTwoColumn}
						style={{
							marginTop: '10px',
							marginBottom: '10px',
						}}
					>
						<SustellMMSAdditionalItem
							formType={formType}
							stageIndex={stageIndex}
							index={index}
							mmsOptions={allMmsOptions}
							storageOptions={allStorageOptions}
							isViewMode={formType === FormType.View || isViewMode}
							fieldItemPrefix={`${fieldItemPrefix}.manureSystemsAdditions`}
							isDairyCowStage={isDairyCowStage}
							handleRemove={removeMMSAdditions}
							isIntervention={true}
						/>
					</div>
				</div>
			))}
			{!isViewMode && <DsmGrid
				className={classes.dsmGridTwoColumnAndIconBtn}
			>
				<div>
					<DsmButton
						variant="text"
						onClick={addMMS}
						disabled={!shouldDisplayMMSRows}
					>
						<DsmIcon slot="before" name="general/plus-circle" />
						{intl.formatMessage({
							id: "SUSTELL.STAGE.POULTRY.HOUSING.ADD_ANOTHER_MMS",
						})}
					</DsmButton>

					<DsmButton
						variant="text"
						onClick={openDescriptionDialog}
						style={{ marginLeft: "20px" }}
					>
						<DsmIcon slot="before" name="general/eye" />
						{intl.formatMessage({ id: "DATABASE_FOUNDATION_DEFINITIONS" })}
					</DsmButton>
				</div>
			</DsmGrid>}
		</div>
	);
};

DairyV2InterventionMMSComponent.defaultProps = {
	stageIndex: 0,
	isViewMode: false,
};

export default DairyV2InterventionMMSComponent;
